import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type KeyboardEventType = 'keydown' | 'keyup' | 'keypress';
type KeyboardEventHandler = (event: KeyboardEvent) => void;

/**
 * Handling keyboard events
 *
 * @param eventType - The type of keyboard event to listen for ('keydown', 'keyup', or 'keypress')
 * @param callback - The callback function to execute when the event occurs
 * @param targetRef - Optional ref for a specific element to attach the listener to (defaults to document)
 * @param options - Additional event listener options
 * @param dependencies - Optional array of dependencies to control when the effect reruns
 */
function useKeyboard(
  eventType: KeyboardEventType,
  callback: KeyboardEventHandler,
  targetRef?: RefObject<HTMLElement>,
  options?: boolean | AddEventListenerOptions,
  dependencies: any[] = [],
): void {
  const callbackRef = useRef<KeyboardEventHandler>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const targetElement: EventTarget = targetRef?.current || document;

    const handler = (event: Event) => {
      callbackRef.current(event as KeyboardEvent);
    };

    targetElement.addEventListener(eventType, handler, options);

    return () => {
      targetElement.removeEventListener(eventType, handler, options);
    };
  }, [eventType, targetRef, options, ...dependencies]);
}

export default useKeyboard;
